/**
 * 发薪日预演面板（F 域 F3 · `DR-052` · 批 87）
 *
 * 依据：`F域PRD` §4.7.2（预演三行 + 可调 + 可跳过）· `AC-F3.2`/`F3.4`/`F3.8`/`F3.9`
 *
 * ## 🔴 本组件**不算数字**
 *
 * 「本次分配后可花」`distributableCents` 是服务端 `composePayday()` 的结论 ——
 * 用户每改一次额度，页面就用新额度**再调一次预演**（接口接受 `allocations`），
 * 面板只负责显示。端侧自己减一遍就会与 J3「本月剩余可花」纠缠（`AC-F3.9` 红线）。
 *
 * ## 🔴 不可分配的目标**不给勾选框**
 *
 * 未绑账户 / 币种不符 / 已达成 —— 显示原因，不给"点了会失败"的开关。
 */

import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  centsToYuanText,
  toCents,
  type PaydayGoalPlanView,
  type PaydayPreviewView,
} from '@jiaying/shared';
import { Amount } from './Amount.js';
import { amountTextToCents } from '../features/goal.js';
import { autoSaveText, payableKindLabel } from '../features/payday.js';
import type { PaydayDraft } from '../features/payday.js';

export interface PaydayPreviewPanelProps {
  readonly preview: PaydayPreviewView;
  readonly draft: PaydayDraft;
  /** 改额度（分）；`0` = 这次不给这个目标攒 */
  readonly onAmountChange: (goalId: string, cents: number) => void;
  readonly disabled?: boolean;
}

/** 一行「标签 + 金额」 */
function MoneyLine({
  label,
  cents,
  currency,
  strong = false,
}: {
  label: string;
  cents: number;
  currency: string;
  strong?: boolean;
}) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ py: 0.5 }}>
      <Typography variant="body2" color={strong ? 'text.primary' : 'text.secondary'}>
        {label}
      </Typography>
      <Amount
        variant={strong ? 'subtitle1' : 'body2'}
        cents={toCents(cents)}
        currency={currency}
        colored={false}
      />
    </Stack>
  );
}

export function PaydayPreviewPanel({
  preview,
  draft,
  onAmountChange,
  disabled = false,
}: PaydayPreviewPanelProps) {
  return (
    <Box data-testid="payday-preview">
      {/* ── ① 三行：实发 − 必付 − 目标攒 = 可花 ── */}
      <Box sx={{ mb: 2 }}>
        <MoneyLine
          label="本次工资到账（实发）"
          cents={preview.netCents as number}
          currency={preview.currency}
        />
        <MoneyLine label="本月必付" cents={preview.payableCents as number} currency={preview.currency} />
        <Divider sx={{ my: 1 }} />
        <MoneyLine
          label="本次分配后可花"
          cents={preview.distributableCents as number}
          currency={preview.currency}
          strong
        />
      </Box>

      {preview.payableIncomplete && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          部分必付项缺汇率，本月必付合计不完整 —— 可花数字会偏大。
        </Alert>
      )}

      {preview.issues.length > 0 && (
        <Alert
          severity={preview.overCommitted ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          data-testid="payday-issues"
        >
          {preview.issues.map((i) => (
            <Typography key={i} variant="body2">
              {i}
            </Typography>
          ))}
        </Alert>
      )}

      {/* ── ② 必付明细（只说"钱被什么占住了"）── */}
      {preview.payableLines.length > 0 && (
        <Box sx={{ mb: 2 }} data-testid="payday-payables">
          <Typography variant="caption" color="text.secondary">
            本月必付明细（{preview.payableLines.length} 项）
          </Typography>
          {preview.payableLines.slice(0, 6).map((l) => (
            <Stack
              key={`${l.kind}-${l.dueDate}-${l.label}`}
              direction="row"
              justifyContent="space-between"
            >
              <Typography variant="caption" color="text.secondary">
                {payableKindLabel(l.kind)} · {l.dueDate} · {l.label}
              </Typography>
              <Amount
                variant="caption"
                cents={l.amountCents}
                currency={preview.currency}
                colored={false}
              />
            </Stack>
          ))}
        </Box>
      )}

      {/* ── ③ 目标：勾选 + 改额 ── */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        给哪些目标攒
      </Typography>
      {preview.goals.length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid="payday-no-goals">
          还没有储蓄目标。可以先只看看「可花」，或去「储蓄目标」建一个。
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />} data-testid="payday-goals">
          {preview.goals.map((g) => (
            <GoalRow
              key={g.goalId}
              goal={g}
              currency={preview.currency}
              checked={draft.amounts.has(g.goalId)}
              amountCents={draft.amounts.get(g.goalId) ?? 0}
              disabled={disabled}
              onChange={onAmountChange}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

function GoalRow({
  goal,
  currency,
  checked,
  amountCents,
  disabled,
  onChange,
}: {
  readonly goal: PaydayGoalPlanView;
  readonly currency: string;
  readonly checked: boolean;
  readonly amountCents: number;
  readonly disabled: boolean;
  readonly onChange: (goalId: string, cents: number) => void;
}) {
  const blocked = goal.blockedReason !== null;
  const suggested = goal.plannedCents as number;

  return (
    <Box sx={{ py: 1, opacity: blocked ? 0.7 : 1 }} data-testid={`payday-goal-${goal.goalId}`}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {/* 🔴 `data-testid` 挂外层 span：MUI 6 的 Checkbox `inputProps` 不接受 `data-*`（铁律 100） */}
        <Box component="span" data-testid={`payday-goal-check-${goal.goalId}`}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={checked}
                disabled={disabled || blocked}
                onChange={(e) => onChange(goal.goalId, e.target.checked ? suggested || 100 : 0)}
              />
            }
            label={<Typography variant="body2">{goal.name}</Typography>}
          />
        </Box>

        {checked && !blocked && (
          <TextField
            size="small"
            label="本期攒"
            value={centsToYuanText(toCents(amountCents))}
            onChange={(e) => {
              const cents = amountTextToCents(e.target.value);
              onChange(goal.goalId, cents === null ? 0 : (cents as number));
            }}
            disabled={disabled}
            sx={{ width: 140 }}
            // 🔴 测试要用 `data-testid` 判断这一行到底有没有输入框（label 文案在 DOM 里会出现两次，不能数字符串）
            inputProps={{ inputMode: 'decimal', 'data-testid': `payday-goal-input-${goal.goalId}` }}
          />
        )}

        <Typography variant="caption" color="text.secondary">
          还差{' '}
          <Amount variant="caption" cents={goal.remainingCents} currency={currency} colored={false} />
          {' · '}
          {autoSaveText(goal.autoSaveMode, goal.autoSaveValue, currency)}
        </Typography>
      </Stack>

      {blocked && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={`payday-goal-blocked-${goal.goalId}`}
        >
          {goal.blockedReason}
        </Typography>
      )}
    </Box>
  );
}
